import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AppErrorCode as LpcAppErrorCode } from '@renovationman/rm-lpc-api-client-ts-ng20';
import { AppErrorCode as UaaAppErrorCode } from '@renovationman/rm-uaa-api-client-ts-ng20';
import { AuthService } from 'libs/auth-lib/src/public-api';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '~env';

import { ApiErrorToastService } from '../services/api-error-toast.service';

enum CustomAppErrorCode {
  EXPIRED_BEARER_TOKEN = 'EXPIRED_BEARER_TOKEN',
}

type ErrorCodes = LpcAppErrorCode | UaaAppErrorCode | CustomAppErrorCode;

interface IApiError {
  statusCode: number;
  status: string;
  message: string;
  errorCode: ErrorCodes;
}

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private apiErrorToastService: ApiErrorToastService
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (req.url.match(this.startWithOneOfRmApiRegexp)) {
      if (this.authService.isTokenExpired()) {
        this.handleError(CustomAppErrorCode.EXPIRED_BEARER_TOKEN);
        return EMPTY;
      }
      return next.handle(req).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.error instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              this.handleError(
                (JSON.parse(reader.result as string) as IApiError).errorCode
              );
            };

            // Start reading the blob as text.
            reader.readAsText(error.error);
          } else {
            this.handleError((error.error as IApiError).errorCode);
          }
          return throwError(error);
        })
      );
    }
    return next.handle(req);
  }

  private handleError(errorCode: ErrorCodes) {
    this.apiErrorToastService.show('API_ERRORS.' + errorCode);
    const loginErrorCodes: ErrorCodes[] = [
      UaaAppErrorCode.INVALID_BEARER_TOKEN,
      UaaAppErrorCode.MISSING_BEARER_TOKEN,
      CustomAppErrorCode.EXPIRED_BEARER_TOKEN,
    ];
    if (loginErrorCodes.includes(errorCode)) {
      this.authService.logout();
    }
  }

  private get startWithOneOfRmApiRegexp(): RegExp {
    const apiBasePathsArray = Object.values(environment.apiBasePaths);
    return new RegExp('^' + apiBasePathsArray.join('|'));
  }
}
